// Frontend-local version of the shared UI component; localization is confined to presentation.
import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { useRef, useState } from "react";
import { useTheme } from "styled-components";
import { Box, HBox, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import { IconCheck } from "@ui/icons/IconCheck";
import { IconClose } from "@ui/icons/IconClose";
import { IconFile } from "@ui/icons/IconFile";
import { Typo } from "@ui/text/Typo";
import { ITheme, RecursiveCSS } from "@ui/theme/GlobalTheme";

interface IFileSelectProps {
	id?: string;
	sx?: RecursiveCSS;
	title?: string;
	subtitle?: string | null;
	name?: string;
	data?: Record<string, any>;
	value?: any;
	disabled?: boolean;
	size?: "small" | "medium" | "large";
	previewMode?: boolean;
	accept?: string;
	onChange?: (file: File | null) => void;
}

export const FileSelect = ({ title, subtitle, size, sx, disabled, previewMode, accept, onChange }: IFileSelectProps) => {
	useLocale();
	const theme = useTheme() as ITheme;
	if (!size) size = "medium";
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [currentFile, setCurrentFile] = useState<File | null>(null);

	const selectFile = () => {
		if (fileInputRef.current) {
			fileInputRef.current.click();
		}
	};

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const files = event.target.files;
		if (files && files.length > 0) {
			const selectedFile = files[0];
			setCurrentFile(selectedFile);
			onChange?.(selectedFile);
		}
	};

	const clearFile = () => {
		setCurrentFile(null);
		if (fileInputRef.current) fileInputRef.current.value = "";
		onChange?.(null);
	};

	//!TODO: get theme color and tint;
	return (
		<HBox
			sx={{
				padding: "10px 10px 10px 18px",
				backgroundColor: currentFile ? theme.colors.success + "25" : "rgba(0,0,0,.06)",
				borderRadius: "6px",
				boxShadow: "0px 1px 2px rgba(0,0,0,.2)",
				gap: "14px",
				minHeight: "62px",
				alignItems: "center",
				...sx,
			}}>
			{currentFile && (
				<Box sx={{ width: "21px", height: "21px" }}>
					<IconCheck />
				</Box>
			)}

			<VBox gap="4px">
				{title && <Typo variant="medium">{title}</Typo>}
				{subtitle && <Typo variant="small">{subtitle}</Typo>}
			</VBox>
			{!currentFile && !previewMode && (
				<Button
					variant="secondarySmall"
					disabled={disabled}
					title={msg("select")}
					onClick={() => {
						selectFile();
					}}
					size={size}
					sx={{ marginLeft: "auto" }}
				/>
			)}
			{currentFile && (
				<HBox
					gap="6px"
					sx={{
						backgroundColor: theme.colors.panelBackgroundPrimary,
						boxShadow: "0px 0px 2px rgba(0,0,0,.25)",
						alignItems: "center",
						maxWidth: "300px",
						minWidth: "180px",
						padding: "6px 10px 6px 10px",
						borderRadius: "4px",
						marginLeft: "auto",
					}}>
					<Box
						sx={{
							width: "24px",
							height: "30px",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}>
						<IconFile color={theme.colors.textPrimary} />
					</Box>

					<VBox sx={{ overflow: "hidden", paddingRight: "8px", marginRight: "auto" }}>
						<Typo
							variant="small"
							color={theme.colors.textPrimary}
							sx={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
							{currentFile.name}
						</Typo>
						<Typo variant="small" color={theme.colors.textSecondary} sx={{}}>
							{currentFile.size / 1024 < 1024
								? (currentFile.size / 1024).toFixed(2) + " KB"
								: (currentFile.size / (1024 * 1024)).toFixed(2) + " MB"}
						</Typo>
					</VBox>

					{!previewMode && (
						<Button
							variant="icon"
							disabled={disabled}
							onClick={clearFile}>
							<Box sx={{ alignItems: "center", display: "flex" }}>
								<IconClose />
							</Box>
						</Button>
					)}
				</HBox>
			)}
			<input
				ref={fileInputRef}
				type="file"
				accept={accept}
				hidden
				onChange={handleFileChange}
			/>
		</HBox>
	);
};
